import React, { useState } from 'react';
import { Box, Alert, Typography, Fab, Chip, Stack, IconButton, Tooltip } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../constants/icons';
import { PAGE_FILL_HEIGHT } from '../constants/layout';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';
import LoadingView from '../components/LoadingView';
import { useCashflow, shiftAnchor } from '../hooks/useCashflow';
import CashflowTable, { SOURCE_STYLE, type CashflowCellContext } from '../components/cashflow/CashflowTable';
import CashflowDetailDialog from '../components/cashflow/CashflowDetailDialog';

const ymLabel = (ym: number | null | undefined): string => (ym ? `${Math.floor(ym / 100)}-${String(ym % 100).padStart(2, '0')}` : '-');

/**
 * 資金繰り表ページ。
 *
 * 実績 3 か月 (OBC 仕訳の現預金増減) と予定 6 か月 (OBC 予定伝票を土台に、買掛金支払は
 * Hozenall NET・工事管理の計上実データ、販管費は予算で上書き) を横並びで表示する。
 * セルをクリックすると取引先別 / 仕訳明細のドリルダウンを開く。
 */
export default function CashflowManagement() {
  const cf = useCashflow();
  const [cellContext, setCellContext] = useState<CashflowCellContext | null>(null);
  const meta = cf.matrix?.meta;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', height: PAGE_FILL_HEIGHT, boxSizing: 'border-box', overflow: 'hidden' }}>
      <Box sx={{ width: '94%', mt: 0, display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
        {/* 見出し + 期間ナビゲーション + 凡例 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}>
              <Icons.Payments /> 資金繰り表
              <span style={{ fontSize: '18px', fontWeight: 500, color: '#64748b' }}>
                - 実績 {cf.monthsBack} か月 + 予定 {cf.monthsAhead} か月
              </span>
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569' }}>
              実績は OBC 仕訳の現預金増減、予定は OBC 予定伝票を土台に買掛金支払 (Hozenall NET・工事管理の計上実データ) と販管費 (予算) で上書き。セルをクリックで明細。
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Tooltip title="当月を 1 か月前へ"><span><IconButton size="small" onClick={() => cf.setAnchor(shiftAnchor(cf.anchor, -1))} disabled={cf.loading}><Icons.ChevronLeft fontSize="small" /></IconButton></span></Tooltip>
              <Chip size="small" label={`当月 ${cf.anchor}`} sx={{ fontSize: '11px', height: '22px', bgcolor: '#eff6ff', color: '#1e3a8a', fontWeight: 600 }} />
              <Tooltip title="当月を 1 か月後へ"><span><IconButton size="small" onClick={() => cf.setAnchor(shiftAnchor(cf.anchor, 1))} disabled={cf.loading}><Icons.ChevronRight fontSize="small" /></IconButton></span></Tooltip>
            </Stack>
            <Chip size="small" label={`計上取込 〜${ymLabel(meta?.payable.last_accrual_month)} (${meta?.payable.row_count ?? 0} 件)`} sx={{ fontSize: '11px', height: '22px' }} />
            <Chip size="small" label={`取込日時 ${meta?.payable.synced_at ? meta.payable.synced_at.slice(0, 16) : '-'}`} sx={{ fontSize: '11px', height: '22px' }} />
            <Fab variant="extended" size="small" onClick={cf.refresh} disabled={cf.loading} sx={STANDARD_FAB_CTA_SX}>
              <Icons.Refresh sx={{ mr: 1 }} /> 再読込
            </Fab>
          </Box>
        </Box>

        {/* 凡例 (セル出所の色) */}
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', px: 0.5 }}>
          {(Object.keys(SOURCE_STYLE) as (keyof typeof SOURCE_STYLE)[]).map(k => (
            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.72rem', color: '#475569' }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: SOURCE_STYLE[k].color }} />
              {SOURCE_STYLE[k].label}
            </Box>
          ))}
          <Box sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>単位 千円 / △ はマイナス</Box>
        </Stack>

        {cf.error && (
          <Alert severity="warning" sx={{ width: '100%', borderRadius: '8px' }}>{cf.error}</Alert>
        )}

        <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {cf.matrix && <CashflowTable matrix={cf.matrix} onCellClick={setCellContext} />}

          {!cf.loading && !cf.error && !cf.matrix && (
            <Box sx={{ mt: 6, p: 5, border: '2px dashed #cbd5e1', borderRadius: '12px', textAlign: 'center' }}>
              <Typography color="textSecondary">データが存在しません。</Typography>
            </Box>
          )}

          {cf.loading && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.65)', borderRadius: '12px', backdropFilter: 'blur(1px)' }}>
              <LoadingView message="資金繰り表を作成中..." height="auto" size={50} />
            </Box>
          )}
        </Box>
      </Box>

      <CashflowDetailDialog context={cellContext} fetchDetail={cf.fetchDetail} onClose={() => setCellContext(null)} />
    </Box>
  );
}
